import { ChangeDetectionStrategy, Component, computed, inject, input, model, output } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountType } from "@front/m19-api-client";
import { AccountGroup, AccountMarketplaceEx, OrganizationAccountGroups } from "@front/m19-models";
import { OrganizationAccountGroupService } from "@front/m19-services";
import { ISelectMenuImports, IDropdownMenuSeparatorComponent } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AccountGroupSelectorOptionComponent } from "./account-group-selector-option.component";

interface CategorizedAccountGroup {
  organizationId: number;
  organizationName: string;
  accountGroups: AccountGroup[];
}

@Component({
  selector: "app-account-group-selector",
  imports: [
    ISelectMenuImports,
    TranslocoDirective,
    AccountGroupSelectorOptionComponent,
    IDropdownMenuSeparatorComponent,
  ],

  templateUrl: "./account-group-selector.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountGroupSelectorComponent {
  private readonly accountGroupService = inject(OrganizationAccountGroupService);
  private readonly translocoService = inject(TranslocoService);

  public readonly selectedAccountGroupId = input<number | undefined>();
  public readonly selectedAccountGroupIds = input<number[]>([]);
  public readonly organizationId = input<number | undefined>(undefined);
  public readonly accountType = input<AccountType | undefined>(undefined);
  public readonly multiSelection = input(false);

  public readonly accountGroupChange = output<AccountGroup>();
  public readonly accountGroupsChange = output<AccountGroup[]>();

  private readonly accountGroups = toSignal<OrganizationAccountGroups[]>(
    this.accountGroupService.allOrganizationAccountGroups$,
    {
      requireSync: true,
    },
  );

  private readonly categorizedAccountGroups = computed<CategorizedAccountGroup[]>(() => {
    const a = this.accountGroups()
      .map((accountGroup) => ({
        organizationId: accountGroup.id,
        organizationName: accountGroup.organizationName,
        accountGroups: this.filterAccountGroups(accountGroup.accountGroups, this.accountType(), this.organizationId()),
      }))
      .filter((categorizedAccountGroup) => categorizedAccountGroup.accountGroups.length > 0);
    return a;
  });

  protected readonly filteredCategorizedAccountGroups = computed<CategorizedAccountGroup[]>(() => {
    const query = this.searchQuery().toLowerCase();
    const filtered: CategorizedAccountGroup[] = [];

    for (const organization of this.categorizedAccountGroups()) {
      const filteredGroups = organization.accountGroups.flatMap((group) => this.filterAccountGroup(group, query));
      if (filteredGroups.length > 0) {
        filtered.push({ ...organization, accountGroups: filteredGroups });
      }
    }

    return filtered;
  });

  private readonly accountGroupsLength = computed<number>(() => {
    return this.categorizedAccountGroups().flatMap((categorizedAccountGroup) => categorizedAccountGroup.accountGroups)
      .length;
  });

  selectedAccountGroup = computed<AccountGroup | undefined>(() => {
    if (!this.selectedAccountGroupId()) return undefined;
    return this.categorizedAccountGroups()
      ?.flatMap((categorizedAccountGroup) => categorizedAccountGroup.accountGroups)
      .find((accountGroup) => accountGroup.id === this.selectedAccountGroupId());
  });

  selectedAccountGroups = computed<AccountGroup[]>(() => {
    return this.categorizedAccountGroups()
      ?.flatMap((categorizedAccountGroup) => categorizedAccountGroup.accountGroups)
      .filter((accountGroup) => this.selectedAccountGroupIds().includes(accountGroup.id));
  });

  selectedAccountGroupsLabel = computed<string>(() => {
    const selection = this.selectedAccountGroups();
    if (selection.length === 0) return this.translocoService.translate("common.none");
    if (selection.length === 1) return selection[0].name;
    if (selection.length === this.accountGroupsLength()) return this.translocoService.translate("common.all_accounts");
    return this.translocoService.translate("account-selector.selected_account", { count: selection.length });
  });

  searchQuery = model<string>("");

  selectOrganization(categorizedAccountGroup: CategorizedAccountGroup) {
    this.accountGroupsChange.emit(categorizedAccountGroup.accountGroups);
  }

  selectAccountGroup(accountGroup: AccountGroup | undefined) {
    if (!accountGroup) return;

    this.accountGroupChange.emit(accountGroup);
  }

  selectAccountGroups(accountGroups: AccountGroup[] | undefined) {
    this.accountGroupsChange.emit(accountGroups ?? []);
  }

  private filterAccountGroups(
    accountGroups: AccountGroup[],
    accountType: AccountType | undefined,
    organizationId: number | undefined,
  ): AccountGroup[] {
    if (!organizationId && !accountType) return accountGroups.sort((a, b) => a.name.localeCompare(b.name));
    return accountGroups
      .filter((accountGroup) => {
        return (
          (accountType ? accountGroup.type === accountType : true) &&
          (organizationId ? accountGroup.organizationId === organizationId : true)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private matchesQuery(query: string, value: string): boolean {
    return value.toLowerCase().includes(query.toLowerCase());
  }

  private filterAccountGroup(group: AccountGroup, query: string): AccountGroup[] {
    const filteredGroups: AccountGroup[] = [];
    if (this.matchesQuery(query, group.name)) {
      filteredGroups.push(group);
    } else {
      const filteredResources = group.resources.filter(
        (resource) => this.matchesQuery(query, resource.accountId) || this.matchesQuery(query, resource.accountName),
      );
      if (filteredResources.length > 0) {
        filteredGroups.push(this.buildAccountGroup(group, filteredResources));
      }
    }

    return filteredGroups;
  }

  private buildAccountGroup(accountGroup: AccountGroup, resources: AccountMarketplaceEx[]): AccountGroup {
    return {
      ...accountGroup,
      resources: resources,
      add: accountGroup.add,
      getAccountMarketplaces: accountGroup.getAccountMarketplaces,
      getAccountMarketplacesWithBidderOn: accountGroup.getAccountMarketplacesWithBidderOn,
      findAccountMarketplace: accountGroup.findAccountMarketplace,
      containsMktp: accountGroup.containsMktp,
      toAccountSelection: accountGroup.toAccountSelection,
    };
  }
}
